// ============================================================================
// JARVISTRAVEL - VERIFY EMAIL
// Post sign-up email confirmation: auto-sends a 6-digit code via Clerk, collects
// it, and confirms ownership through Clerk's sign-up flow. Falls back to the
// app's own /auth/verify-email API when the Clerk sign-up context isn't available
// (e.g. settings email change, forgot password).
// ============================================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { MailCheck, Check } from 'lucide-react';
import { useSignUp } from '@clerk/clerk-react';
import { taskRouter } from './sessionTask';
import { clerkErrorMessage } from '../../utils/clerkError';
import { Button, Modal, CodeInput, type CodeInputHandle } from '@/app/components/shared';
import { sendVerificationCode, verifyEmail } from '@/app/api/auth';
import { CODE_LENGTH, emptyCode } from './emailCode';


interface VerifyEmailScreenProps {
  email: string;
  /**
   * Where to send someone whose new session carries an outstanding Clerk task,
   * which on this instance means setting up a second factor. Optional because
   * the routing belongs to the app, not to this screen (JAR-1700).
   */
  onSessionTask?: (path: string) => void;
  /** Called once the email is confirmed. */
  onVerified: () => void;
  /** Called when the user chooses to verify later. */
  onSkip: () => void;
  /** Where to go when the email is verified but signing in failed (JAR-2499). */
  onSignIn: () => void;
}

/**
 * This screen keeps a THIRD category the shared helper does not have: fetch
 * throws a TypeError on DNS and connection failures, and telling someone their
 * code was wrong when the request never left the device is the wrong message.
 *
 * Order is preserved exactly. Clerk is still tested first, and the network case
 * is passed as the non-Clerk fallback rather than tested before it — a
 * ClerkAPIResponseError is never a TypeError, so the two orders agree, and this
 * one leaves the shared helper in charge of the Clerk branch.
 */
function verifyErrorMessage(e: unknown, fallback: string): string {
  return clerkErrorMessage(
    e,
    fallback,
    e instanceof TypeError
      ? 'Unable to connect. Please check your internet connection and try again.'
      : fallback,
  );
}

export function VerifyEmailScreen({ email, onVerified, onSkip, onSignIn, onSessionTask }: VerifyEmailScreenProps) {
  const [code, setCode] = useState<string[]>(emptyCode);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [resending, setResending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // The email is verified but the session would not activate. Nothing is left
  // to verify, so the form gives way to signing in (JAR-2499).
  const [activationFailed, setActivationFailed] = useState(false);
  const codeRef = useRef<CodeInputHandle>(null);

  // Clerk sign-up context — available after SignUpScreen calls create() +
  // prepareEmailAddressVerification(). If null (page refresh, non-sign-up flow)
  // we fall back to the app's own API.
  // Use a ref to track the latest Clerk state so async handlers always read
  // fresh context, avoiding stale-closure races (H2).
  const { isLoaded, signUp, setActive } = useSignUp();
  const clerkCtxRef = useRef({ isLoaded, signUp, setActive });
  clerkCtxRef.current = { isLoaded, signUp, setActive };
  // Clerk has no user until the sign-up completes, so App can pass an empty
  // address. The sign-up knows it, including after a reload (JAR-2492).
  const sentTo = email || signUp?.emailAddress || '';

  const send = useCallback(async () => {
    try {
      const { isLoaded: freshLoaded, signUp: freshSignUp } = clerkCtxRef.current;
      const freshClerkContext = freshLoaded && freshSignUp?.status === 'missing_requirements';
      if (freshClerkContext) {
        // Auto-send via Clerk so the code matches what the user received
        await freshSignUp!.prepareEmailAddressVerification({ strategy: 'email_code' });
      } else {
        await sendVerificationCode(email);
      }
      setCountdown(60);
    } catch (e) {
      console.error('[verify-email] send failed:', e);
      // Clear countdown so the user can retry immediately
      setCountdown(0);
      setError(verifyErrorMessage(e, 'We couldn\'t send a code. Use Resend to try again.'));
    }
  }, [email]);

  // Whether this screen owes a code is decided ONCE, when it opens, and the ref
  // also guards StrictMode's double run. It used to wait for an address: a
  // sign-up opens this screen with none (Clerk has no user yet, and has sent its
  // own code), and the address arrives the moment Clerk signs the person in. By
  // then the sign-up is `complete`, so `send` took the fallback and asked core
  // for a code. Core answers a verified address with its password-reset email,
  // and every new account got "Reset your password" on its way to two-factor
  // setup (JAR-3094).
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    if (email) send();
  }, [email, send]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleCodeChange = (next: string[]) => {
    setCode(next);
    setError('');
  };

  const verify = async (full: string) => {
    if (full.length !== CODE_LENGTH || isVerifying) {
      if (full.length !== CODE_LENGTH) setError('Please enter the complete 6-digit code.');
      return;
    }
    setIsVerifying(true);
    setError('');
    try {
      // Read fresh Clerk context at call time to avoid stale-closure races (H2)
      const { isLoaded: freshLoaded, signUp: freshSignUp } = clerkCtxRef.current;
      const freshClerkContext = freshLoaded && freshSignUp?.status === 'missing_requirements';

      if (freshClerkContext) {
        // Use Clerk's verification when coming from the sign-up flow — the code
        // the user received in their email was sent by Clerk (via
        // prepareEmailAddressVerification in SignUpScreen). This ensures the
        // code they enter matches what Clerk expects.
        const result = await freshSignUp!.attemptEmailAddressVerification({ code: full });
        if (result.status === 'complete' || result.status === 'missing_requirements') {
          // status 'missing_requirements' means email is verified but other
          // fields may still be pending — that's fine, the sign-up is done.
          //
          // A verified sign-up is not a signed-in person until the session is
          // activated: Clerk hands back `createdSessionId` and waits. And the
          // production instance requires MFA, so the session it activates is
          // PENDING, carrying a setup-mfa task that has to be finished before
          // anything else. `taskRouter` takes them there; a session with no
          // task navigates nowhere and this screen's own Continue still rules.
          const { setActive: freshSetActive } = clerkCtxRef.current;
          if (result.createdSessionId && freshSetActive) {
            const task = taskRouter(onSessionTask);
            try {
              await freshSetActive({
                session: result.createdSessionId,
                navigate: task.navigate,
              });
            } catch (activationError) {
              // THE EMAIL IS VERIFIED from here. A session that fails to
              // activate is not a rejected code, and the catch below would call
              // it one — sending someone back to re-enter a one-time code Clerk
              // has already consumed, which can only fail (web-app#440 review;
              // the same rule useSecondFactor follows after web-app#384).
              console.error('[verify-email] session activation failed:', activationError);
              setError('Your email is verified, but we couldn\'t sign you in. Please sign in to continue.');
              setActivationFailed(true);
              return;
            }
            // The enrolment screen owns the page now. A success modal on top of
            // it is a dialog over a screen the person cannot get back to.
            if (task.routed) return;
          }
          setShowSuccess(true);
        } else {
          setError('Verification failed. Please try again.');
        }
      } else {
        // Fallback: use the app's own verification API (settings email
        // change, forgot password flows, or page refresh after sign-up).
        await verifyEmail(email, full);
        setShowSuccess(true);
      }
    } catch (e) {
      console.error('[verify-email] verify failed:', e);
      setError(verifyErrorMessage(e, 'That code didn\'t work. Check it and try again.'));
    } finally {
      setIsVerifying(false);
    }
  };
  const handleVerify = () => verify(code.join(''));

  const handleResend = async () => {
    if (countdown > 0 || resending) return;
    setResending(true);
    setError('');
    try {
      // Read fresh Clerk context at call time (H2)
      const { isLoaded: freshLoaded, signUp: freshSignUp } = clerkCtxRef.current;
      const freshClerkContext = freshLoaded && freshSignUp?.status === 'missing_requirements';

      if (freshClerkContext) {
        // Guard: don't resend if signUp has progressed past missing_requirements (L7)
        if (freshSignUp!.status !== 'missing_requirements') {
          setError('Verification is no longer pending. Please refresh and try again.');
          return;
        }
        // Resend via Clerk so the new code matches Clerk's verification.
        await freshSignUp!.prepareEmailAddressVerification({ strategy: 'email_code' });
      } else {
        // Fallback: resend via the app's own API.
        await sendVerificationCode(email);
      }
      setCountdown(60);
      setCode(emptyCode());
      codeRef.current?.focus();
    } catch (e) {
      console.error('[verify-email] resend failed:', e);
      setError(verifyErrorMessage(e, 'We couldn\'t resend the code. Please try again.'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-bg flex flex-col">
      <div className="flex-1 px-6 py-10 flex items-center justify-center">
        <div className="bg-surface border border-border rounded-3xl shadow-xl shadow-black/5 p-6 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto bg-accent/10 rounded-2xl flex items-center justify-center text-accent mb-4">
              <MailCheck className="w-8 h-8" strokeWidth={1.75} aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-ink">Verify your email</h1>
            <p className="text-muted mt-2">
              {sentTo ? (
                <>We sent a 6-digit code to <span className="font-medium text-ink">{sentTo}</span>.</>
              ) : (
                'Enter the 6-digit code from your email.'
              )}
            </p>
          </div>

          {activationFailed ? (
            <div className="space-y-5">
              <p role="alert" className="text-sm text-danger text-center">{error}</p>
              <Button onClick={onSignIn} variant="primary" fullWidth size="lg">Sign in</Button>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-body mb-3 text-center">Enter the code</label>
                <CodeInput ref={codeRef} value={code} onChange={handleCodeChange} onComplete={verify} error={!!error} />
                {error && <p className="text-sm text-danger text-center mt-2">{error}</p>}
              </div>

              <Button onClick={handleVerify} variant="primary" fullWidth size="lg" loading={isVerifying}>Verify email</Button>

              <p className="text-sm text-muted text-center">
                Didn&apos;t get it?{' '}
                {countdown > 0 ? (
                  <span>Resend in {countdown}s</span>
                ) : (
                  <button onClick={handleResend} disabled={resending} className="text-accent font-medium hover:opacity-80 disabled:opacity-50">Resend code</button>
                )}
              </p>

              <button onClick={onSkip} className="w-full text-sm text-muted hover:text-body py-1">I&apos;ll verify later</button>
            </div>
          )}
        </div>
      </div>

      {/* Success requires an explicit "Continue" — backdrop/Escape must not advance. */}
      <Modal isOpen={showSuccess} onClose={() => {}} showCloseButton={false} size="sm">
        <div className="text-center py-4">
          <div className="w-20 h-20 mx-auto bg-success/10 rounded-full flex items-center justify-center text-success mb-4">
            <Check className="w-10 h-10" strokeWidth={2} aria-hidden="true" />
          </div>
          <h3 className="text-2xl font-bold text-ink mb-2">Email verified</h3>
          <p className="text-muted mb-6">Thanks, your email address is confirmed.</p>
          <Button onClick={onVerified} variant="primary" fullWidth size="lg">Continue</Button>
        </div>
      </Modal>
    </div>
  );
}

export default VerifyEmailScreen;
